/**
 * モーショングラフィックス生成の「AIモデル」の選択 (Claude Opus 5.5 / Gemini 4 Argon・2026-10-16 利用者決定)。
 * MotionQualitySelect (仕上げ方) と並べて置く。背景・枠線なしの「モデル名 + シェブロン」のボタンで、押すとドロップダウンを開く
 * (2026-10-16 利用者指示: 横並びのタブではなくドロップダウンに)。メニューはシナリオ画面のモデル選択と同じ ModelDropdownMenuShell。
 * 状態は features/motion/model の全経路共通のストア (localStorage 'motion_generation_model')。
 */
import {
  MOTION_MODEL_HEADING,
  MOTION_MODEL_LABELS,
  MOTION_MODELS,
  RECOMMENDED_MOTION_MODEL,
  useMotionModel,
} from '../../../features/motion/model';
import { RecommendedBadge } from '../../common/RecommendedBadge';
import { usePortalDropdown } from '../scenario/hooks/usePortalDropdown';
import { ModelDropdownItem, ModelDropdownMenuShell, ModelDropdownSectionHeader } from './ModelDropdownMenu';

// 外側クリックの判定に使うだけのクラス名 (スタイルは持たない)
const TRIGGER_CLASS = 'motion-model-select-trigger';

export function MotionModelSelect({
  disabled,
  compact = false,
  testId = 'motion-model',
}: {
  disabled?: boolean;
  /** true: 見出し「AIモデル」を出さない (シーンの動画欄など狭い場所) */
  compact?: boolean;
  testId?: string;
}) {
  const [model, setModel] = useMotionModel();
  const dropdown = usePortalDropdown({ triggerClassName: TRIGGER_CLASS });
  const isOpen = dropdown.openId === testId;
  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(245, 241, 230, 0.9)', opacity: disabled ? 0.6 : 1 }}
      onClick={(e) => e.stopPropagation()}
    >
      {!compact && <span style={{ whiteSpace: 'nowrap' }}>{MOTION_MODEL_HEADING}</span>}
      <button
        type="button"
        className={TRIGGER_CLASS}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${MOTION_MODEL_HEADING}: ${MOTION_MODEL_LABELS[model]}`}
        data-testid={testId}
        onClick={(e) => dropdown.open(testId, e.currentTarget)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0,
          background: 'none', border: 'none', color: 'inherit', fontSize: 12, whiteSpace: 'nowrap',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {MOTION_MODEL_LABELS[model]}
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ transition: 'transform 0.15s', transform: isOpen ? 'rotate(180deg)' : undefined }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && dropdown.pos && (
        <ModelDropdownMenuShell menuRef={dropdown.menuRef} pos={dropdown.pos} emptyText="">
          {[
            <ModelDropdownSectionHeader key="hdr" label={MOTION_MODEL_HEADING} />,
            ...MOTION_MODELS.map((m) => (
              <div key={m} data-testid={`${testId}-${m}`} data-active={model === m ? 'true' : 'false'}>
                <ModelDropdownItem
                  label={(
                    <>
                      {m === RECOMMENDED_MOTION_MODEL && <RecommendedBadge active={model === m} />}
                      <span>{MOTION_MODEL_LABELS[m]}</span>
                    </>
                  )}
                  active={model === m}
                  onSelect={() => {
                    setModel(m);
                    dropdown.close();
                  }}
                />
              </div>
            )),
          ]}
        </ModelDropdownMenuShell>
      )}
    </div>
  );
}
